import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import CompetitorMisspelledWords from './CompetitorMisspelledWords';

const SpellingPractice = ({ selectedBee, selectedCompetitor, onBackToBeeSelection }) => {
  const { user } = useAuth();
  const [currentWord, setCurrentWord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalAttempts: 0,
    correctCount: 0,
    unsureCount: 0,
    wrongCount: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMisspelledWords, setShowMisspelledWords] = useState(false);
  const isDevelopment = process.env.REACT_APP_ENV === 'development';
  const useLocalStack = process.env.REACT_APP_USE_LOCALSTACK === 'true';

  useEffect(() => {
    fetchNextWord();
  }, [selectedBee]);

  const fetchNextWord = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (isDevelopment && !useLocalStack) {
        // Mock word data for demo (without LocalStack)
        const mockWords = [
          'restaurant', 'necessary', 'definitely', 'separate', 'weird', 'achieve',
          'cemetery', 'rhythm', 'acknowledge', 'embarrass', 'privilege', 'occurrence',
          'discipline', 'maintenance', 'conscience', 'fluorescent', 'pneumonia', 'psychology'
        ];
        
        const randomWord = mockWords[Math.floor(Math.random() * mockWords.length)];
        
        const mockResponse = {
          word: randomWord,
          bee_id: selectedBee.bee_id,
          metadata: {
            total_attempts: Math.floor(Math.random() * 5),
            last_result: Math.random() > 0.5 ? 'correct' : 'wrong',
            next_review_date: new Date().toISOString(),
            difficulty_score: Math.floor(Math.random() * 100)
          }
        };
        
        // Simulate API delay
        setTimeout(() => {
          setCurrentWord(mockResponse);
          setLoading(false);
        }, 600);
      } else {
        // Real API call for production - use competitor if available
        const response = selectedCompetitor
          ? await apiService.getNextWordForCompetitor(selectedCompetitor.competitor_id, selectedBee.bee_id)
          : await apiService.getNextWord(user.user_id, selectedBee.bee_id);

        // Debug logging to see the response structure
        console.log('Lambda response:', JSON.stringify(response, null, 2));
        if (response.metadata) {
          console.log('Metadata:', JSON.stringify(response.metadata, null, 2));
        }

        setCurrentWord(response);
        setLoading(false);
      }
      
    } catch (err) {
      setError('Failed to load next word. Please try again.');
      setLoading(false);
    }
  };

  const handleResponse = async (result) => {
    if (isSubmitting || !currentWord) return;

    try {
      setIsSubmitting(true);
      setError(null);

      if (isDevelopment && !useLocalStack) {
        // Simulate API delay for demo (mock mode only)
        setTimeout(async () => {
          // Update stats
          setStats(prevStats => ({
            ...prevStats,
            totalAttempts: prevStats.totalAttempts + 1,
            [`${result}Count`]: prevStats[`${result}Count`] + 1
          }));

          // Fetch next word immediately
          await fetchNextWord();
          setIsSubmitting(false);
        }, 400);
      } else {
        // Real API calls for production - use competitor if available
        if (selectedCompetitor) {
          await apiService.writeAttemptForCompetitor(
            selectedCompetitor.competitor_id,
            selectedBee.bee_id,
            currentWord.word,
            result,
            user.user_id
          );
        } else {
          await apiService.writeAttempt(
            user.user_id,
            selectedBee.bee_id,
            currentWord.word,
            result
          );
        }

        // Update stats
        setStats(prevStats => ({
          ...prevStats,
          totalAttempts: prevStats.totalAttempts + 1,
          [`${result}Count`]: prevStats[`${result}Count`] + 1
        }));

        // Fetch next word immediately
        await fetchNextWord();
        setIsSubmitting(false);
      }
      
    } catch (err) {
      setError('Failed to submit response. Please try again.');
      setIsSubmitting(false);
    }
  };


  const getAccuracyPercentage = () => {
    if (stats.totalAttempts === 0) return 0;
    return Math.round((stats.correctCount / stats.totalAttempts) * 100);
  };


  // Show misspelled words view if requested
  if (showMisspelledWords && selectedBee && selectedCompetitor) {
    return (
      <CompetitorMisspelledWords
        competitor={selectedCompetitor}
        selectedBee={selectedBee}
        onBack={() => setShowMisspelledWords(false)}
      />
    );
  }

  if (loading && !currentWord) {
    return (
      <div className="card">
        <div className="loading">Loading your next word...</div>
      </div>
    );
  }

  return (
    <div className="card">
      {/* Header */}
      <div style={{ marginBottom: '0.5rem', textAlign: 'center' }}>
        <h2 className="title" style={{ fontSize: '1.8rem', margin: '0' }}>
          {selectedBee.name}
        </h2>
        {selectedCompetitor && (
          <p style={{ color: '#007bff', margin: '0.5rem 0 0 0', fontWeight: '500' }}>
            Playing as: {selectedCompetitor.name}
          </p>
        )}
      </div>

      {/* Stats - Two Line Layout */}
      <div style={{
        marginBottom: '1.5rem',
        padding: '0.75rem 1rem',
        background: '#f7fafc',
        borderRadius: '8px',
        textAlign: 'center',
        fontSize: '0.9rem',
        color: '#4a5568'
      }}>
        {/* First line: Total and Accuracy */}
        <div style={{ marginBottom: '0.25rem' }}>
          <span style={{ fontWeight: '600', color: '#2d3748' }}>
            {stats.totalAttempts} total
          </span>
          <span style={{ margin: '0 0.75rem', color: '#e2e8f0' }}>•</span>
          <span style={{ fontWeight: '600', color: '#667eea' }}>
            {getAccuracyPercentage()}% accuracy
          </span>
        </div>

        {/* Second line: Breakdown */}
        <div>
          <span style={{ fontWeight: '600', color: '#48bb78' }}>
            {stats.correctCount} correct
          </span>
          <span style={{ margin: '0 0.75rem', color: '#e2e8f0' }}>•</span>
          <span style={{ fontWeight: '600', color: '#ed8936' }}>
            {stats.unsureCount} unsure
          </span>
          <span style={{ margin: '0 0.75rem', color: '#e2e8f0' }}>•</span>
          <span style={{ fontWeight: '600', color: '#f56565' }}>
            {stats.wrongCount} wrong
          </span>
        </div>
      </div>

      {error && (
        <div className="error">{error}</div>
      )}

      {currentWord ? (
        <>
          {/* Word Display */}
          <div className="word-display" style={{
            color: currentWord.word_source === 'ai' ? '#8b5cf6' : '#2d3748'
          }}>
            {currentWord.word}
            {currentWord.word_source === 'ai' && (
              <div style={{
                fontSize: '0.75rem',
                fontWeight: '500',
                color: '#8b5cf6',
                marginTop: '0.5rem',
                letterSpacing: '0.5px'
              }}>
                AI-GENERATED
              </div>
            )}
          </div>

          {/* Last 10 Attempts Summary */}
          {currentWord.metadata && currentWord.metadata.last_10_attempts && currentWord.metadata.last_10_attempts.total > 0 && (
            <div style={{
              fontSize: '0.9rem',
              color: '#718096',
              marginBottom: '1rem',
              textAlign: 'center',
              padding: '0.75rem',
              background: '#f7fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#2d3748' }}>
                Last {currentWord.metadata.last_10_attempts.total} attempts:
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                {currentWord.metadata.last_10_attempts.correct > 0 && (
                  <span style={{ color: '#48bb78', fontWeight: '600' }}>
                    ✓ {currentWord.metadata.last_10_attempts.correct}
                  </span>
                )}
                {currentWord.metadata.last_10_attempts.unsure > 0 && (
                  <span style={{ color: '#ed8936', fontWeight: '600' }}>
                    ? {currentWord.metadata.last_10_attempts.unsure}
                  </span>
                )}
                {currentWord.metadata.last_10_attempts.wrong > 0 && (
                  <span style={{ color: '#f56565', fontWeight: '600' }}>
                    ✗ {currentWord.metadata.last_10_attempts.wrong}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Response Buttons */}
          <div className="response-buttons">
            <button
              className="response-button correct"
              onClick={() => handleResponse('correct')}
              disabled={isSubmitting}
              title="I spelled this correctly"
            >
              ✓
            </button>
            <button
              className="response-button unsure"
              onClick={() => handleResponse('unsure')}
              disabled={isSubmitting}
              title="I'm not sure about this"
            >
              ?
            </button>
            <button
              className="response-button wrong"
              onClick={() => handleResponse('wrong')}
              disabled={isSubmitting}
              title="I got this wrong"
            >
              ✗
            </button>
          </div>

          {/* Misspelled Words Button - Only show for competitors */}
          {selectedCompetitor && (
            <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
              <button
                onClick={() => setShowMisspelledWords(true)}
                className="button"
                style={{ background: '#fd7e14', fontSize: '0.9rem' }}
              >
                View Misspelled Words (48h)
              </button>
            </div>
          )}

          {isSubmitting && (
            <div className="loading" style={{ fontSize: '1rem', margin: '1rem 0' }}>
              Submitting response...
            </div>
          )}
        </>
      ) : (
        <div className="loading">No words available for this spelling bee.</div>
      )}
    </div>
  );
};

export default SpellingPractice;